import { useEffect, useState } from 'react';

import getCustomer from '@/shared/api/customers/getCustomer';
import viewPayments from '@/shared/api/orders/payments/viewPayments';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Customer from '@/shared/types/customer.type';
import Payment from '@/shared/types/order/payment.type';
import Response from '@/shared/types/response';

type Args = {
  transactionId: string;
};

const usePaymentDetailsData = ({ transactionId }: Args) => {
  const redirectLogin = useRedirectLogin();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [error, setError] = useState<Response<unknown> | null>(null);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCustomer = async (customerId: string) => {
      const response = await getCustomer(customerId, redirectLogin);

      if (response.success) {
        setCustomer(response.data[0]);
        setLoading(false);
      } else {
        setError({
          data: response.data,
          msg: response.msg,
          success: response.success,
          status: response.status,
        });
        setLoading(false);
      }
    };

    const fetchPayment = async () => {
      const paymentResponse = await viewPayments({
        body: {
          search: [
            {
              in_transaction_id: {
                cop: ['='],
                value: transactionId,
              },
            },
          ],
        },
        onUnauthorized: redirectLogin,
      });

      if (paymentResponse.success) {
        const paymentData = paymentResponse.data[0];

        await fetchCustomer(paymentData.in_customer_id);
        setPayment(paymentData);
        setLoading(false);
      } else {
        setError({
          data: paymentResponse.data,
          msg: paymentResponse.msg,
          success: paymentResponse.success,
          status: paymentResponse.status,
        });
        setLoading(false);
      }
    };

    void fetchPayment();
  }, [redirectLogin, transactionId]);

  return { customer, payment, loading, error };
};

export default usePaymentDetailsData;
